import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, LockKey, GitBranch, Plus, Copy, Check, Trash, FileArrowUp,
    Receipt, Buildings, Calendar, Hash, FileText, Scales, ArrowRight, Coins,
    PencilSimple, ChartLineUp, Lightning, Paperclip, Eye, DownloadSimple,
} from "@phosphor-icons/react";
import { api, formatApiError, previewDocument, downloadDocument } from "../lib/api";
import { formatINR, formatDate, formatDateTime } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import OwnerCell from "../components/OwnerCell";
import ReassignOwnerDialog from "../components/ReassignOwnerDialog";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import CatalogueProductPicker from "../lib/CatalogueProductPicker";
import { expandLineItem, expandLineItems, formatScopeChannels } from "../lib/proposalPackageDisplay";
import { CommitmentsBadgeStrip } from "../components/proposals/CommitmentsBadgeStrip";
import { SpecialOfferDisplay } from "../components/proposals/SpecialOfferDisplay";
import { CommercialSummaryRows } from "../components/billing/CommercialSummary";
import AmcPoolPanel from "../components/billing/AmcPoolPanel";
import SubscriptionPoolPanel from "../components/billing/SubscriptionPoolPanel";
import { PoProposalCoverageCard } from "../components/billing/ProposalPoCoverage";
import { usesAnnualCost } from "../lib/lineItemFees";
import { CapabilityBadges } from "../components/catalogue/CapabilityBadges";
import { InvoicePrintBody } from "./InvoicePrint";
import { toast } from "sonner";

const emptyLine = {
    product_name: "",
    product_type: "One-time",
    amount: 0,
    amc_amount: 0,
    billing_frequency: "Annual",
    start_date: "",
    end_date: "",
    gst_applicable: true,
    gst_percent: 18,
    hsn_sac_code: "997331",
    // Iter C.2 — Package snapshot pass-through. The PO dialog does not
    // author packages (that's done upstream at the Proposal authoring
    // stage per Phase C D3 immutability). Package fields here are
    // preserved verbatim across an atomic-style edit so an operator
    // tweaking a HSN code or GST percent on a package line item doesn't
    // accidentally drop the package context.
    package_source_id: null,
    package_source_code: null,
    package_presentation_mode: null,
    package_consolidated_label: null,
    package_components: [],
};

const PRESENTATION_MODE_OPTIONS = [
    { value: "itemized",     label: "Itemized" },
    { value: "consolidated", label: "Consolidated" },
    { value: "hybrid",       label: "Hybrid" },
];

function VersionTimeline({ po, selectedId, onSelect }) {
    const sorted = [...po.versions].sort((a, b) => a.version_number - b.version_number);
    return (
        <div className="reedos-card p-1" data-testid="version-timeline">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><GitBranch size={12} /> Version history</div>
                    <div className="text-xs text-slate-500 mt-0.5">{sorted.length} version{sorted.length > 1 ? "s" : ""}</div>
                </div>
            </div>
            <div className="p-2 space-y-1">
                {sorted.map((v) => {
                    const isFinal = v.is_final_version;
                    const isSelected = v.id === selectedId;
                    return (
                        <button
                            key={v.id}
                            type="button"
                            onClick={() => onSelect(v.id)}
                            data-testid={`version-tab-${v.version_number}`}
                            className={cn(
                                "w-full text-left rounded-md border px-3 py-2.5 transition-colors",
                                isSelected
                                    ? "border-slate-900 bg-slate-900 text-white"
                                    : isFinal
                                        ? "border-amber-200 bg-amber-50 hover:border-amber-400"
                                        : "border-slate-200 bg-white hover:border-slate-400"
                            )}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className={cn("font-mono text-sm font-bold", isSelected ? "text-white" : "text-slate-900")}>
                                    V{v.version_number}
                                </span>
                                {isFinal && (
                                    <span className={cn(
                                        "inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded",
                                        isSelected ? "bg-white/20 text-white" : "bg-amber-200 text-amber-900"
                                    )}>
                                        <LockKey size={10} weight="fill" /> LOCKED
                                    </span>
                                )}
                            </div>
                            <div className={cn("text-[11px] mt-1 line-clamp-2", isSelected ? "text-white/80" : "text-slate-500")}>
                                {v.remarks || "—"}
                            </div>
                            <div className={cn("text-[10px] mt-1.5", isSelected ? "text-white/60" : "text-slate-400")}>
                                {formatDateTime(v.created_date)}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function LineItemDialog({ poId, versionId, onAdded, initial = null, open: controlledOpen, onOpenChange }) {
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (v) => {
        if (isControlled) onOpenChange?.(v);
        else setInternalOpen(v);
    };
    const isEdit = !!initial;
    const [form, setForm] = useState(emptyLine);
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef(JSON.stringify(emptyLine));

    useEffect(() => {
        if (!open) return;
        let next;
        if (isEdit && initial) {
            next = {
                product_name: initial.product_name || "",
                product_type: initial.product_type || "One-time",
                amount: initial.amount ?? 0,
                amc_amount: initial.amc_amount ?? 0,
                billing_frequency: initial.billing_frequency || "Annual",
                gst_applicable: initial.gst_applicable !== false,
                gst_percent: initial.gst_percent ?? 18,
                start_date: initial.start_date || "",
                end_date: initial.end_date || "",
                hsn_sac_code: initial.hsn_sac_code || "997331",
                // Iter C.2 — Preserve package fields verbatim across edit.
                package_source_id: initial.package_source_id || null,
                package_source_code: initial.package_source_code || null,
                package_presentation_mode: initial.package_presentation_mode || null,
                package_consolidated_label: initial.package_consolidated_label || null,
                package_components: initial.package_components || [],
            };
        } else {
            next = emptyLine;
        }
        setForm(next);
        baselineRef.current = JSON.stringify(next);
    }, [open, isEdit, initial]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                amount: Number(form.amount),
                amc_amount: Number(form.amc_amount || 0),
                gst_percent: Number(form.gst_percent || 0),
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                // Annual Cost fields are not edited here — carried through unchanged.
                ...(isEdit ? {
                    annual_cost_amount: initial.annual_cost_amount ?? null,
                    annual_cost_pricing_mode: initial.annual_cost_pricing_mode ?? null,
                    amc_pricing_mode: initial.amc_pricing_mode ?? null,
                } : {}),
            };
            if (isEdit) {
                await api.patch(
                    `/billing/po/${poId}/lineitem/${initial.id}?version_id=${versionId}`,
                    payload,
                );
                toast.success("Line item updated");
            } else {
                await api.post(`/billing/po/${poId}/lineitem/add?version_id=${versionId}`, payload);
                toast.success("Line item added");
            }
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            setOpen(false);
            if (!isEdit) setForm(emptyLine);
            onAdded?.();
        } catch (err) {
            toast.error(formatApiError(err, isEdit ? "Failed to update line item" : "Failed to add line item"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {!isControlled && (
                <Button
                    data-testid="add-line-item-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setOpen(true)}
                    className="gap-1.5"
                >
                    <Plus size={14} weight="bold" /> Add line item
                </Button>
            )}
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-lg"
                testId="line-item-dialog"
                title={isEdit ? "Edit line item" : "Add line item"}
                description={isEdit ? "Updates the selected line on this version." : "This will be added to the currently selected version."}
            >
                <form onSubmit={submit} className="space-y-3" data-testid="line-item-form">
                    <div>
                        <Label className="text-xs font-semibold">Product / service</Label>
                        <div className="mt-1.5">
                            <CatalogueProductPicker
                                value={form.product_name}
                                onChange={(v, item) => setForm((p) => ({
                                    ...p,
                                    product_name: v,
                                    // Iter 38: auto-fill HSN/SAC + GST from catalogue snapshot.
                                    // Operator can still override either field below.
                                    ...(item ? {
                                        hsn_sac_code: item.hsn_sac_code || p.hsn_sac_code || "997331",
                                        gst_percent: item.default_gst_percent ?? p.gst_percent,
                                    } : {}),
                                }))}
                                testidPrefix="po-li"
                                required
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Type</Label>
                            <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="li-type-select">
                                <option>One-time</option>
                                <option>AMC</option>
                            </select>
                        </div>
                        {/* Iter 51.8 — Frequency is meaningless for One-time line items;
                            hide the field entirely so the operator can't enter a contradictory
                            "One-time · Annual" combination. */}
                        {form.product_type !== "One-time" ? (
                            <div>
                                <Label className="text-xs font-semibold">Billing frequency</Label>
                                <select value={form.billing_frequency} onChange={(e) => setForm({ ...form, billing_frequency: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="li-frequency-select">
                                    <option>Annual</option>
                                    <option>Half-Yearly</option>
                                    <option>Quarterly</option>
                                    <option>Monthly</option>
                                </select>
                            </div>
                        ) : (
                            <div className="text-[10px] text-slate-400 pt-7 italic" data-testid="li-frequency-na">
                                Frequency does not apply to One-time items.
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Amount (₹)</Label>
                            <Input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 font-mono" data-testid="li-amount-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">GST %</Label>
                            <Input type="number" min="0" max="28" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="mt-1.5 font-mono" data-testid="li-gst-input" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">HSN / SAC code</Label>
                            <Input
                                value={form.hsn_sac_code || ""}
                                onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value.replace(/\s+/g, "") })}
                                placeholder="997331"
                                maxLength={20}
                                className="mt-1.5 font-mono"
                                data-testid="li-hsn-input"
                            />
                            <p className="mt-1 text-[10px] text-slate-500">Auto-filled from catalogue. Override here if needed.</p>
                        </div>
                        <div className="flex items-end pb-1">
                            <p className="text-[10px] text-slate-400 leading-snug">
                                <span className="font-semibold text-slate-500">Snapshotted</span> at the line-item level — historical PO / invoice copies stay immutable.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Start date</Label>
                            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="li-start-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">End date</Label>
                            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" data-testid="li-end-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <FormDialogCancel testId="li-cancel-btn">Cancel</FormDialogCancel>
                        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="li-submit-btn">
                            {submitting ? (isEdit ? "Saving..." : "Adding...") : (isEdit ? "Save changes" : "Add line")}
                        </Button>
                    </DialogFooter>
                </form>
            </FormDialog>
        </>
    );
}

function CloneVersionButton({ poId, versionId, onCloned }) {
    const [open, setOpen] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/billing/po/${poId}/version/clone`, { source_version_id: versionId, remarks });
            toast.success("Version cloned");
            setOpen(false);
            setRemarks("");
            onCloned?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to clone version"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="clone-version-btn">
                    <Copy size={14} /> Clone
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-display">Clone version</DialogTitle>
                    <DialogDescription>Copies all line items into a new version. You can edit afterwards.</DialogDescription>
                </DialogHeader>
                <div>
                    <Label className="text-xs font-semibold">Remarks for the new version</Label>
                    <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="What's changing?" rows={2} className="mt-1.5" data-testid="clone-remarks-input" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="clone-submit-btn">
                        {submitting ? "Cloning..." : "Clone version"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FinalizeButton({ poId, version, onDone }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const empty = !version.line_items || version.line_items.length === 0;
    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/billing/po/${poId}/finalize`, { version_id: version.id });
            toast.success(`V${version.version_number} marked as Final · PO locked`);
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to finalize"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" disabled={empty} className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5" data-testid="finalize-version-btn">
                    <LockKey size={14} weight="fill" /> Finalize this version
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-display">Finalize V{version.version_number}?</DialogTitle>
                    <DialogDescription>
                        This locks the entire PO. No further versions or edits will be allowed. The PO becomes ready for invoicing.
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    Once finalized, you can only raise invoices against the finalized version.
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="finalize-confirm-btn">
                        {submitting ? "Finalizing..." : "Yes, finalize & lock"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function GenerateInvoiceButton({ po, onCreated }) {
    const [open, setOpen] = useState(false);
    const bs = po.billing_status || { po_total: 0, balance_to_invoice: 0, invoiced_amount: 0, net_po_total: 0, net_balance_to_invoice: 0 };
    // Iter 67 — when a Special Offer is applied, milestone preview
    // amounts and the "Balance to invoice" check must use net (post-
    // discount) figures so what the operator sees in the preview
    // matches the invoice the backend actually persists. Falls back
    // to legacy gross fields for un-discounted POs (backend
    // preserves net_* == po_* in that case).
    const offerActive = !!(po.special_offer && po.special_offer.enabled);
    // POAGG-001 (2026-02-13) — When the PO carries AMC value (atomic
    // amc_amount, package_components[].amc_amount, commitments tagged
    // AMC, or AMC-typed atomic line), the backend's
    // /invoice/from-po endpoint sizes the invoice against the
    // Implementation-only bucket. AMC value is billed exclusively by
    // the AMC scheduler from each schedule's activation date. The
    // dialog mirrors this split so the operator's preview matches what
    // the backend will actually persist (no surprises at submit time).
    const hasAmcValue = !!bs.has_amc_value;
    const implPoTotal = bs.implementation_po_total ?? bs.net_po_total ?? bs.po_total;
    const implBalance = bs.implementation_balance_to_invoice ?? bs.net_balance_to_invoice ?? bs.balance_to_invoice;
    const amcPoTotal = bs.amc_po_total ?? 0;
    const fullNetPoTotal = offerActive ? (bs.net_po_total ?? bs.po_total) : bs.po_total;
    const fullNetBalance = offerActive ? (bs.net_balance_to_invoice ?? bs.balance_to_invoice) : bs.balance_to_invoice;
    const poTotalForBilling = hasAmcValue ? implPoTotal : fullNetPoTotal;
    const balanceForBilling = hasAmcValue ? implBalance : fullNetBalance;
    const schedule = po.payment_schedule || [];
    const billedMilestoneIds = new Set((po.invoices || []).map((i) => i.milestone_id).filter(Boolean));
    const fullyBilled = balanceForBilling <= 0.01 && poTotalForBilling > 0;

    // Mode: 'milestone' | 'percent' | 'manual' | 'full'
    const [mode, setMode] = useState("full");
    const [milestoneId, setMilestoneId] = useState("");
    const [percent, setPercent] = useState(50);
    const [manualAmount, setManualAmount] = useState("");
    // UAT-001 (2026-02-12) — Fixed-amount mode sub-toggle. Operator picks
    // whether the entered figure is the taxable base (default — matches
    // operator mental anchor for historical-data migration) or the
    // GST-inclusive grand total (cheque-matching workflow).
    const [manualBasis, setManualBasis] = useState("taxable"); // 'taxable' | 'inclusive'
    const [label, setLabel] = useState("");
    const [paymentTerms, setPaymentTerms] = useState("30 days");
    const [notes, setNotes] = useState("");
    // Iter 2026-02 · Operator directive — optional Start / End dates on
    // the from-PO invoice. Required for products with no setup effort
    // (content delivery, live POC, subscription-only), where the
    // invoice's "when" is the entire commercial value. Persisted to
    // `billing_start_date` / `billing_end_date` on the invoice; the
    // print PDF renders them under the invoice number as
    // `Term: 01 Jul 2026 → 30 Jun 2027` when set. Left blank the PDF
    // omits the term line (backward compatible with legacy invoices).
    const [billingStartDate, setBillingStartDate] = useState("");
    const [billingEndDate, setBillingEndDate] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // Iter 2026-02 · Invoice Preview UAT — non-mutating dry-run render.
    // Operator clicks "Preview" → we call /invoice/from-po/{id}/preview
    // (dry_run=True, zero DB writes, invoice_number="PREVIEW") and open
    // a large modal rendering `InvoicePrintBody` with the exact same
    // PDF layout the real invoice would produce. Company info is
    // fetched once and cached in `previewCompany`.
    const [previewData, setPreviewData] = useState(null);
    const [previewCompany, setPreviewCompany] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // UAT-001 — Effective GST factor derived from the PO's frozen line items.
    // For an 18% uniform PO this is 1.18; for heterogeneous lines it is the
    // weighted average. Falls back to 1.0 (no GST) if the PO has zero pre-tax
    // value or no GST is applicable. Matches backend math at
    // routes_invoices.py: target_total = taxable_amount * (net_incl / net_excl)
    const effectiveGstFactor = useMemo(() => {
        const offerActive2 = !!(po.special_offer && po.special_offer.enabled);
        const netExcl = offerActive2 ? (bs.net_po_excl_gst ?? bs.po_excl_gst) : bs.po_excl_gst;
        const netIncl = offerActive2 ? (bs.net_po_total ?? bs.po_total) : bs.po_total;
        if (!netExcl || netExcl <= 0) return 1.0;
        return netIncl / netExcl;
    }, [po, bs]);
    const effectiveGstPercent = Math.max(0, (effectiveGstFactor - 1) * 100);

    // Resolve preview of the target invoice total (GST-inclusive — what the
    // backend will persist as the invoice's total_amount).
    const previewAmount = useMemo(() => {
        if (mode === "milestone" && milestoneId) {
            const m = schedule.find((x) => x.id === milestoneId);
            if (m) return Math.round((poTotalForBilling * m.percent / 100) * 100) / 100;
        }
        if (mode === "percent") return Math.round((poTotalForBilling * Number(percent) / 100) * 100) / 100;
        if (mode === "manual") {
            const entered = Number(manualAmount) || 0;
            // UAT-001 — In Taxable basis, the inclusive total is entered * factor.
            // In Inclusive basis, the entered figure IS the inclusive total.
            return manualBasis === "taxable"
                ? Math.round(entered * effectiveGstFactor * 100) / 100
                : entered;
        }
        if (mode === "full") return balanceForBilling;
        return 0;
    }, [mode, milestoneId, percent, manualAmount, manualBasis, effectiveGstFactor, schedule, poTotalForBilling, balanceForBilling]);

    // UAT-001 — Inline preview for the manual mode (Taxable / GST / Total split).
    const manualPreview = useMemo(() => {
        if (mode !== "manual") return null;
        const entered = Number(manualAmount) || 0;
        if (entered <= 0) return null;
        let taxable, gst, total;
        if (manualBasis === "taxable") {
            taxable = entered;
            total = Math.round(entered * effectiveGstFactor * 100) / 100;
            gst = Math.round((total - taxable) * 100) / 100;
        } else {
            total = entered;
            taxable = effectiveGstFactor > 0
                ? Math.round((entered / effectiveGstFactor) * 100) / 100
                : entered;
            gst = Math.round((total - taxable) * 100) / 100;
        }
        return { taxable, gst, total };
    }, [mode, manualAmount, manualBasis, effectiveGstFactor]);

    const exceeds = previewAmount > balanceForBilling + 0.01;

    // Iter 2026-02 · Shared payload builder — used by both submit and
    // openPreview so what the operator sees in the preview is exactly
    // what the persist endpoint receives when they click Generate.
    const buildPayload = () => {
        const payload = {
            payment_terms: paymentTerms,
            notes: notes,
            invoice_label: label?.trim() || null,
            billing_start_date: billingStartDate || null,
            billing_end_date: billingEndDate || null,
        };
        if (mode === "milestone") payload.milestone_id = milestoneId;
        else if (mode === "percent") payload.percent_of_po = Number(percent);
        else if (mode === "manual") {
            if (manualBasis === "taxable") payload.taxable_amount = Number(manualAmount);
            else payload.fixed_amount = Number(manualAmount);
        }
        // mode === 'full' sends nothing extra → backend invoices the full balance
        return payload;
    };

    const openPreview = async () => {
        setPreviewLoading(true);
        try {
            const payload = buildPayload();
            const requests = [api.post(`/billing/invoice/from-po/${po.id}/preview`, payload)];
            if (!previewCompany) requests.push(api.get("/billing/company-info"));
            const results = await Promise.all(requests);
            const invRes = results[0];
            const companyRes = results[1];
            setPreviewData(invRes.data);
            if (companyRes) setPreviewCompany(companyRes.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to build preview"));
        } finally {
            setPreviewLoading(false);
        }
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = buildPayload();
            const { data } = await api.post(`/billing/invoice/from-po/${po.id}`, payload);
            toast.success(`Invoice ${data.invoice_number} generated for ${formatINR(data.total_amount, po?.currency)}`);
            setPreviewData(null);
            setOpen(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to generate invoice"));
        } finally {
            setSubmitting(false);
        }
    };

    const ModeChip = ({ value, label: modeLabel, hint }) => (
        <button
            type="button"
            onClick={() => setMode(value)}
            className={`text-left px-3 py-2 rounded-md border-2 transition-colors ${mode === value ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
            data-testid={`inv-mode-${value}`}
        >
            <div className="font-semibold text-xs text-slate-900">{modeLabel}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
        </button>
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    disabled={fullyBilled}
                    className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                    data-testid="generate-invoice-btn"
                >
                    <Receipt size={14} /> {fullyBilled ? "Fully invoiced" : "Generate invoice"}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">Generate invoice from PO {po.po_number}</DialogTitle>
                    <DialogDescription>
                        {hasAmcValue ? (
                            <>Implementation balance {formatINR(balanceForBilling, po?.currency)} · Already invoiced {formatINR(bs.implementation_invoiced_amount ?? bs.invoiced_amount, po?.currency)} of Implementation Net {formatINR(poTotalForBilling, po?.currency)}{offerActive && <span className="text-rose-600 ml-1">(after special offer)</span>}</>
                        ) : (
                            <>PO total {formatINR(poTotalForBilling, po?.currency)} · Already invoiced {formatINR(bs.invoiced_amount, po?.currency)} · Balance to invoice {formatINR(balanceForBilling, po?.currency)}{offerActive && <span className="text-rose-600 ml-1">(after special offer)</span>}</>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* POAGG-001 (2026-02-13) — Dual-bucket informational banner.
                    Shown only when the PO carries AMC value so the operator
                    immediately understands why /invoice/from-po sizes against
                    the Implementation portion. AMC cycles are emitted by the
                    AMC scheduler from each schedule's activation date — never
                    via this dialog. */}
                {hasAmcValue && (
                    <div
                        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs space-y-2"
                        data-testid="inv-impl-vs-amc-banner"
                    >
                        <div className="font-semibold text-amber-900">
                            This PO carries AMC value — billed separately by the AMC scheduler
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded bg-white px-3 py-2 border border-amber-100">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                    Implementation (this dialog)
                                </div>
                                <div className="font-mono font-semibold text-slate-900 mt-1" data-testid="inv-impl-bucket-total">
                                    {formatINR(implPoTotal, po?.currency)}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                    Balance {formatINR(implBalance, po?.currency)}
                                </div>
                            </div>
                            <div className="rounded bg-white px-3 py-2 border border-amber-100">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                    AMC value (scheduler-billed)
                                </div>
                                <div className="font-mono font-semibold text-slate-900 mt-1" data-testid="inv-amc-bucket-total">
                                    {formatINR(amcPoTotal, po?.currency)}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                    From AMC schedule activation date
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mode picker */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="inv-mode-picker">
                    {schedule.length > 0 && <ModeChip value="milestone" label="By milestone" hint="From PO schedule" />}
                    <ModeChip value="percent" label="By %" hint="Custom % of PO" />
                    <ModeChip value="manual" label="Fixed amount" hint="Taxable or Inclusive" />
                    <ModeChip value="full" label="Full balance" hint="Bill entire remaining" />
                </div>

                {/* Mode-specific inputs */}
                {mode === "milestone" && (
                    <div data-testid="inv-milestone-picker">
                        <Label className="text-xs font-semibold">Milestone</Label>
                        <select
                            value={milestoneId}
                            onChange={(e) => {
                                setMilestoneId(e.target.value);
                                const m = schedule.find((x) => x.id === e.target.value);
                                if (m) setLabel(m.label);
                            }}
                            className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        >
                            <option value="">— Select milestone —</option>
                            {schedule.map((m) => {
                                const billed = billedMilestoneIds.has(m.id);
                                return (
                                    <option key={m.id} value={m.id} disabled={billed}>
                                        {m.label} · {m.percent}% ({formatINR(poTotalForBilling * m.percent / 100, po?.currency)}){billed ? "  ✓ already invoiced" : ""}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
                {mode === "percent" && (
                    <div data-testid="inv-percent-picker">
                        <Label className="text-xs font-semibold">Percentage of PO</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            {[25, 50, 75, 100].map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setPercent(p)}
                                    className={`px-3 py-1.5 rounded-md border text-xs font-semibold ${Number(percent) === p ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                                    data-testid={`inv-percent-${p}`}
                                >
                                    {p}%
                                </button>
                            ))}
                            <Input
                                type="number"
                                min="0.1"
                                max="100"
                                step="0.5"
                                value={percent}
                                onChange={(e) => setPercent(e.target.value)}
                                className="font-mono w-24"
                            />
                            <span className="text-sm text-slate-500">%</span>
                        </div>
                    </div>
                )}
                {mode === "manual" && (
                    <div data-testid="inv-manual-input" className="space-y-3">
                        {/* UAT-001 (2026-02-12) — Basis selector. Default is
                            Taxable to match the operator's natural mental
                            anchor for historical-data migration. Inclusive
                            remains available for cheque-matching workflows. */}
                        <div>
                            <Label className="text-xs font-semibold">What does this amount represent?</Label>
                            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="inv-manual-basis">
                                <button
                                    type="button"
                                    onClick={() => setManualBasis("taxable")}
                                    className={`text-left px-3 py-2 rounded-md border-2 transition-colors ${manualBasis === "taxable" ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
                                    data-testid="inv-manual-basis-taxable"
                                >
                                    <div className="font-semibold text-xs text-slate-900">Taxable amount</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">Pre-GST · system adds GST</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setManualBasis("inclusive")}
                                    className={`text-left px-3 py-2 rounded-md border-2 transition-colors ${manualBasis === "inclusive" ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
                                    data-testid="inv-manual-basis-inclusive"
                                >
                                    <div className="font-semibold text-xs text-slate-900">GST-inclusive total</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">Customer-payable · system splits GST out</div>
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">
                                {manualBasis === "taxable"
                                    ? "Taxable amount (₹, excl. GST)"
                                    : "Customer-payable (₹, incl. GST)"}
                            </Label>
                            <Input
                                type="number"
                                min="0.01"
                                step="100"
                                value={manualAmount}
                                onChange={(e) => setManualAmount(e.target.value)}
                                placeholder={`Max ${formatINR(
                                    manualBasis === "taxable" && effectiveGstFactor > 0
                                        ? balanceForBilling / effectiveGstFactor
                                        : balanceForBilling,
                                    po?.currency,
                                )}`}
                                className="mt-1.5 font-mono"
                                data-testid="inv-manual-amount-input"
                            />
                        </div>
                        {/* UAT-001 — Live inline split preview using the PO's
                            frozen effective GST factor. Operator sees the
                            taxable / GST / total split BEFORE clicking Generate. */}
                        {manualPreview && (
                            <div
                                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
                                data-testid="inv-manual-preview"
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    Computed split
                                    {effectiveGstPercent > 0 && (
                                        <span className="ml-1 font-mono text-slate-400">
                                            (effective GST {effectiveGstPercent.toFixed(2)}%)
                                        </span>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="text-[10px] text-slate-500">Taxable</div>
                                        <div className="font-mono font-semibold text-slate-900" data-testid="inv-manual-preview-taxable">
                                            {formatINR(manualPreview.taxable, po?.currency)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-slate-500">GST</div>
                                        <div className="font-mono font-semibold text-slate-900" data-testid="inv-manual-preview-gst">
                                            {formatINR(manualPreview.gst, po?.currency)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-slate-500">Invoice total</div>
                                        <div className="font-mono font-semibold text-slate-900" data-testid="inv-manual-preview-total">
                                            {formatINR(manualPreview.total, po?.currency)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <Label className="text-xs font-semibold">Invoice label (auto-fills from milestone)</Label>
                    <Input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder='e.g. "Advance Invoice", "Milestone 2: UAT"'
                        className="mt-1.5"
                        data-testid="inv-label-input"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Payment terms</Label>
                        <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="inv-terms-select">
                            <option>Immediate</option>
                            <option>15 days</option>
                            <option>30 days</option>
                            <option>45 days</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Notes <span className="font-normal text-slate-400">(optional)</span></Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid="inv-notes-input" />
                    </div>
                </div>

                {/* Iter 2026-02 · Operator directive — Optional service Term
                    for products delivered instantly (content, live POC,
                    subscription). Renders on the invoice PDF as
                    `Term: 01 Jul 2026 → 30 Jun 2027`. Leave blank for
                    invoices where the service period is derived from a
                    milestone or is not applicable. */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Service start <span className="font-normal text-slate-400">(optional)</span></Label>
                        <Input
                            type="date"
                            value={billingStartDate}
                            onChange={(e) => setBillingStartDate(e.target.value)}
                            max={billingEndDate || undefined}
                            className="mt-1.5"
                            data-testid="inv-billing-start-input"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Service end <span className="font-normal text-slate-400">(optional)</span></Label>
                        <Input
                            type="date"
                            value={billingEndDate}
                            onChange={(e) => setBillingEndDate(e.target.value)}
                            min={billingStartDate || undefined}
                            className="mt-1.5"
                            data-testid="inv-billing-end-input"
                        />
                    </div>
                </div>
                {billingStartDate && billingEndDate && billingStartDate > billingEndDate && (
                    <div className="text-[11px] text-rose-700 font-semibold -mt-2" data-testid="inv-date-range-error">
                        Service start date cannot be after service end date.
                    </div>
                )}

                {/* Preview strip */}
                <div className={`rounded-md border px-4 py-3 ${exceeds ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="inv-preview-strip">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="reedos-eyebrow">Invoice will be raised for</div>
                            <div className={`font-display font-bold text-2xl mt-1 reedos-data ${exceeds ? "text-rose-700" : "text-emerald-800"}`}>
                                {formatINR(previewAmount, po?.currency)}
                            </div>
                        </div>
                        <div className="text-right text-[11px] text-slate-600">
                            <div>PO balance: {formatINR(balanceForBilling, po?.currency)}</div>
                            <div className="text-slate-500">After this: {formatINR(Math.max(0, balanceForBilling - previewAmount), po?.currency)}</div>
                        </div>
                    </div>
                    {exceeds && (
                        <div className="text-[11px] text-rose-700 font-semibold mt-1.5">
                            Exceeds remaining PO balance — adjust amount.
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="outline"
                        onClick={openPreview}
                        disabled={
                            previewLoading || submitting || exceeds || previewAmount <= 0
                            || (mode === "milestone" && !milestoneId)
                            || (billingStartDate && billingEndDate && billingStartDate > billingEndDate)
                        }
                        className="gap-1.5 border-slate-300"
                        data-testid="invoice-preview-btn"
                    >
                        <Eye size={14} /> {previewLoading ? "Building preview…" : "Preview invoice"}
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={
                            submitting || exceeds || previewAmount <= 0
                            || (mode === "milestone" && !milestoneId)
                            || (billingStartDate && billingEndDate && billingStartDate > billingEndDate)
                        }
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="generate-invoice-confirm-btn"
                    >
                        {submitting ? "Generating..." : `Generate invoice ${formatINR(previewAmount, po?.currency)}`}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Iter 2026-02 · Invoice Preview modal — renders the exact
                PDF layout `InvoicePrintBody` will produce, using the dry-run
                response from POST /invoice/from-po/{po.id}/preview. Nothing
                is persisted; closing the modal returns the operator to the
                Generate Invoice dialog with all fields intact so they can
                make further edits and re-preview, or click Generate. */}
            <Dialog open={!!previewData} onOpenChange={(v) => { if (!v) setPreviewData(null); }}>
                <DialogContent
                    className="max-w-5xl max-h-[90vh] overflow-y-auto p-0"
                    data-testid="invoice-preview-dialog"
                >
                    <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 bg-white sticky top-0 z-10">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <DialogTitle className="font-display flex items-center gap-2">
                                    <Eye size={18} />
                                    Invoice preview
                                    <span
                                        className="ml-1 text-[10px] font-bold tracking-wider uppercase text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5"
                                        data-testid="invoice-preview-badge"
                                    >
                                        Draft · Not saved
                                    </span>
                                </DialogTitle>
                                <DialogDescription className="text-xs">
                                    This is exactly how the invoice PDF will render. Nothing is saved yet — close to edit, or Generate to commit.
                                </DialogDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setPreviewData(null)}
                                    className="text-slate-700"
                                    data-testid="invoice-preview-close-btn"
                                >
                                    Close
                                </Button>
                                <Button
                                    onClick={() => { setPreviewData(null); submit(); }}
                                    disabled={submitting}
                                    className="bg-slate-900 hover:bg-slate-800 text-white"
                                    data-testid="invoice-preview-generate-btn"
                                >
                                    {submitting ? "Generating…" : `Generate invoice ${formatINR(previewAmount, po?.currency)}`}
                                </Button>
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="bg-slate-100 px-6 py-6" style={{ position: "relative" }}>
                        {previewData && previewCompany && (
                            <InvoicePrintBody invoice={previewData} company={previewCompany} isPreview={true} />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

function ClientPoRefCard({ po, onChanged }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        client_po_received: po.client_po_received || false,
        client_po_number: po.client_po_number || "",
        client_po_date: po.client_po_date || "",
        po_source_type: po.po_source_type || "system_generated",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setForm({
            client_po_received: po.client_po_received || false,
            client_po_number: po.client_po_number || "",
            client_po_date: po.client_po_date || "",
            po_source_type: po.po_source_type || "system_generated",
        });
    }, [po]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/billing/po/${po.id}/client-ref`, {
                ...form,
                client_po_document_id: po.client_po_document_id || null,
            });
            toast.success("Client PO reference updated");
            setOpen(false);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        } finally {
            setSaving(false);
        }
    };

    const uploadClientPoDocument = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("entity_type", "po");
            fd.append("entity_id", po.id);
            const { data: doc } = await api.post("/billing/document/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Wire the uploaded doc as the client-PO document on the PO record
            await api.patch(`/billing/po/${po.id}/client-ref`, {
                client_po_received: po.client_po_received || true,
                client_po_number: po.client_po_number || "",
                client_po_date: po.client_po_date || null,
                po_source_type: "client_provided",
                client_po_document_id: doc.id,
            });
            toast.success("Client PO document attached");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const removeClientPoDocument = async () => {
        if (!window.confirm("Remove the attached client PO document? The file remains on the server but will no longer be linked to this PO.")) return;
        try {
            await api.patch(`/billing/po/${po.id}/client-ref`, {
                client_po_received: po.client_po_received || false,
                client_po_number: po.client_po_number || "",
                client_po_date: po.client_po_date || null,
                po_source_type: po.po_source_type || "system_generated",
                client_po_document_id: null,
            });
            toast.success("Client PO document unlinked");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to unlink document"));
        }
    };

    const isClientProvided = po.po_source_type === "client_provided";
    const cpDoc = po.client_po_document; // resolved by backend in po_get

    return (
        <div className="reedos-card p-5" data-testid="po-client-ref-card">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="reedos-eyebrow flex items-center gap-2">
                        <Hash size={11} /> Client PO Reference
                        {isClientProvided
                            ? <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[9px] font-bold border bg-emerald-50 text-emerald-800 border-emerald-200">CLIENT PROVIDED</span>
                            : <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[9px] font-bold border bg-slate-50 text-slate-700 border-slate-300">SYSTEM GENERATED</span>}
                    </div>
                    {po.client_po_received && po.client_po_number ? (
                        <>
                            <div className="font-mono text-base font-bold text-slate-900 mt-1">{po.client_po_number}</div>
                            {po.client_po_date && (
                                <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                                    <Calendar size={11} /> dated {formatDate(po.client_po_date)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-sm text-slate-500 italic mt-1">
                            No client PO recorded yet — manually entered based on agreed proposal.
                        </div>
                    )}
                </div>
                <RoleGuard permission="po:write">
                    <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1" data-testid="po-client-ref-edit-btn">
                        <PencilSimple size={12} /> Edit
                    </Button>
                </RoleGuard>
            </div>

            {/* Client PO Document — upload / preview / download */}
            <div className="mt-4 pt-4 border-t border-dashed border-slate-200" data-testid="client-po-doc-block">
                <div className="reedos-eyebrow flex items-center gap-1.5 mb-2">
                    <Paperclip size={11} /> Client PO Document
                </div>
                {cpDoc ? (
                    <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="client-po-doc-attached">
                        <FileText size={20} className="text-slate-500 shrink-0" weight="duotone" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900 truncate">{cpDoc.filename}</div>
                            <div className="text-[11px] text-slate-500">
                                {(cpDoc.size / 1024).toFixed(1)} KB · uploaded {formatDateTime(cpDoc.uploaded_at)}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={async () => {
                                try { await previewDocument(cpDoc.id, { mime: cpDoc.content_type }); }
                                catch (err) { toast.error(formatApiError(err, "Preview failed")); }
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-400 transition-colors"
                            data-testid="client-po-doc-preview-btn"
                        >
                            <Eye size={12} /> Preview
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                try { await downloadDocument(cpDoc.id, cpDoc.filename); }
                                catch (err) { toast.error(formatApiError(err, "Download failed")); }
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-400 transition-colors"
                            data-testid="client-po-doc-download-btn"
                        >
                            <DownloadSimple size={12} /> Download
                        </button>
                        <RoleGuard permission="po:write">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={removeClientPoDocument}
                                className="h-8 px-2 text-slate-400 hover:text-rose-600"
                                title="Unlink document"
                                data-testid="client-po-doc-remove-btn"
                            >
                                <Trash size={13} />
                            </Button>
                        </RoleGuard>
                    </div>
                ) : (
                    <RoleGuard permission="po:write">
                        <label
                            className="block border-2 border-dashed border-slate-200 bg-slate-50 rounded-md px-4 py-4 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition-colors"
                            data-testid="client-po-doc-upload-zone"
                        >
                            <FileArrowUp size={18} className="mx-auto text-slate-400" />
                            <div className="text-xs text-slate-600 mt-1.5 font-medium">
                                {uploading ? "Uploading client PO…" : "Upload client PO PDF / image"}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">PDF / JPG / PNG · max 15 MB</div>
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => uploadClientPoDocument(e.target.files?.[0])}
                                accept=".pdf,image/*"
                                disabled={uploading}
                                data-testid="client-po-doc-upload-input"
                            />
                        </label>
                    </RoleGuard>
                )}
                {!cpDoc && !po.client_po_received && (
                    <p className="text-[11px] text-slate-400 italic mt-2">
                        Tip: attach the actual PDF the client emailed/issued so audit + finance always have the source document one click away.
                    </p>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Client PO Reference</DialogTitle>
                        <DialogDescription>
                            Record the client's actual PO number/date when they formally issue one. If the client doesn't follow PO practice, leave "Client PO received" unchecked.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="po-client-received-toggle">
                            <input
                                type="checkbox"
                                checked={form.client_po_received}
                                onChange={(e) => setForm((p) => ({
                                    ...p,
                                    client_po_received: e.target.checked,
                                    po_source_type: e.target.checked ? "client_provided" : "system_generated",
                                }))}
                                className="w-4 h-4 accent-slate-900"
                            />
                            Client provided their own PO
                        </label>
                        <div>
                            <Label className="text-xs font-semibold">Client PO number</Label>
                            <Input
                                value={form.client_po_number}
                                onChange={(e) => setForm({ ...form, client_po_number: e.target.value })}
                                placeholder="e.g. ACME/PO/2026/0042"
                                disabled={!form.client_po_received}
                                className="mt-1.5 font-mono disabled:bg-slate-50"
                                data-testid="po-client-number-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Client PO date</Label>
                            <Input
                                type="date"
                                value={form.client_po_date}
                                onChange={(e) => setForm({ ...form, client_po_date: e.target.value })}
                                disabled={!form.client_po_received}
                                className="mt-1.5 disabled:bg-slate-50"
                                data-testid="po-client-date-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="po-client-ref-save-btn">
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PoBillingStatusCard({ po }) {
    const bs = po.billing_status;
    if (!bs) return null;
    // Iter 67 — when a Special Offer is applied, the "PO Total" and
    // "Balance to invoice" tiles must show the post-discount figures
    // so the at-a-glance Billing Status reconciles to what invoices
    // are actually being raised at. ``net_po_total`` equals
    // ``po_total`` when no offer is applied (backend preserves this
    // invariant), so the swap is safe on un-discounted POs.
    const offerActive = !!(po.special_offer && po.special_offer.enabled);
    const totalForTile = offerActive ? (bs.net_po_total ?? bs.po_total) : bs.po_total;
    const balanceForTile = offerActive ? (bs.net_balance_to_invoice ?? bs.balance_to_invoice) : bs.balance_to_invoice;
    const fullyBilledForTile = offerActive ? !!bs.net_fully_invoiced : !!bs.fully_invoiced;
    // POAGG-001 (2026-02-13) — When the PO carries AMC value, surface
    // the Implementation vs AMC split alongside the standard ledger
    // tiles. Operators need to immediately see:
    //   1. The full contract value (PO Total — what the client signed)
    //   2. The currently invoiceable Implementation balance (via
    //      /invoice/from-po)
    //   3. The AMC value that is billed separately by the AMC scheduler
    // The legacy "Balance to invoice" tile keeps its meaning (= full
    // PO balance, regardless of bucket) so existing dashboards do not
    // regress. The new Implementation / AMC tiles are additive.
    const hasAmcValue = !!bs.has_amc_value;
    const tiles = [
        { label: offerActive ? "PO Total (after offer)" : "PO Total", value: totalForTile, tone: "slate", testid: "bs-total" },
        { label: "Invoiced", value: bs.invoiced_amount, tone: "indigo", testid: "bs-invoiced" },
        { label: "Received", value: bs.received_amount, tone: "emerald", testid: "bs-received" },
        { label: "Balance to invoice", value: balanceForTile, tone: fullyBilledForTile ? "emerald" : "amber", testid: "bs-balance" },
        { label: "Outstanding", value: bs.outstanding_amount, tone: bs.fully_paid ? "emerald" : "rose", testid: "bs-outstanding" },
    ];
    return (
        <div className="reedos-card p-5" data-testid="po-billing-status-card">
            <div className="flex items-center gap-2 mb-3">
                <ChartLineUp size={14} className="text-slate-700" />
                <div className="reedos-eyebrow">Billing Status</div>
                {offerActive && (
                    <span
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold border bg-rose-50 text-rose-700 border-rose-200"
                        data-testid="bs-offer-applied-badge"
                        title="Special Offer is applied — figures shown are net of the offer."
                    >
                        Offer applied
                    </span>
                )}
                {fullyBilledForTile && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold border bg-emerald-50 text-emerald-800 border-emerald-200">
                        <Check size={10} weight="bold" /> Fully invoiced
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {tiles.map((t) => {
                    const toneText = {
                        slate: "text-slate-900",
                        indigo: "text-indigo-700",
                        emerald: "text-emerald-700",
                        amber: "text-amber-700",
                        rose: "text-rose-700",
                    }[t.tone];
                    return (
                        <div key={t.label} className="rounded-md border border-slate-200 px-3 py-2" data-testid={t.testid}>
                            <div className="reedos-eyebrow text-[9px]">{t.label}</div>
                            <div className={`reedos-data font-display font-bold text-base mt-0.5 ${toneText}`}>{formatINR(t.value, po?.currency)}</div>
                        </div>
                    );
                })}
            </div>
            {hasAmcValue && (
                <div
                    className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5"
                    data-testid="po-impl-vs-amc-split"
                >
                    <div className="flex items-center gap-1.5 mb-2">
                        <div className="reedos-eyebrow text-[9px] text-amber-900">
                            Implementation vs AMC split
                        </div>
                        <span className="text-[10px] text-amber-800">
                            (PO carries AMC value billed by the AMC scheduler — not via Generate Invoice)
                        </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="rounded bg-white px-2.5 py-1.5 border border-amber-100" data-testid="bs-impl-total">
                            <div className="reedos-eyebrow text-[9px]">Implementation Net</div>
                            <div className="reedos-data font-display font-bold text-sm mt-0.5 text-slate-900">
                                {formatINR(bs.implementation_po_total, po?.currency)}
                            </div>
                        </div>
                        <div className="rounded bg-white px-2.5 py-1.5 border border-amber-100" data-testid="bs-impl-balance">
                            <div className="reedos-eyebrow text-[9px]">Implementation Balance</div>
                            <div className="reedos-data font-display font-bold text-sm mt-0.5 text-amber-700">
                                {formatINR(bs.implementation_balance_to_invoice, po?.currency)}
                            </div>
                        </div>
                        <div className="rounded bg-white px-2.5 py-1.5 border border-amber-100" data-testid="bs-amc-total">
                            <div className="reedos-eyebrow text-[9px]">AMC Net</div>
                            <div className="reedos-data font-display font-bold text-sm mt-0.5 text-slate-900">
                                {formatINR(bs.amc_po_total, po?.currency)}
                            </div>
                        </div>
                        <div className="rounded bg-white px-2.5 py-1.5 border border-amber-100">
                            <div className="reedos-eyebrow text-[9px]">AMC Channel</div>
                            <div className="text-[10px] mt-1 text-slate-700">
                                Scheduler-billed from activation date
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {bs.invoice_count > 0 && (
                <div className="mt-3 text-[11px] text-slate-500">
                    {bs.invoice_count} invoice{bs.invoice_count !== 1 ? "s" : ""} raised against this PO.
                </div>
            )}
        </div>
    );
}

function PaymentScheduleCard({ po, onChanged }) {
    const [open, setOpen] = useState(false);
    const [milestones, setMilestones] = useState([]);
    const [saving, setSaving] = useState(false);
    const billedMilestoneIds = new Set((po.invoices || []).map((i) => i.milestone_id).filter(Boolean));
    // Iter 67 — milestone % previews must reflect the post-discount
    // PO total when a Special Offer is applied so the operator's
    // expected billing amount matches what the backend will persist.
    const offerActive = !!(po.special_offer && po.special_offer.enabled);
    const poTotalBasis = offerActive
        ? (po.billing_status?.net_po_total ?? po.billing_status?.po_total ?? 0)
        : (po.billing_status?.po_total ?? 0);

    useEffect(() => {
        setMilestones((po.payment_schedule || []).map((m) => ({ ...m })));
    }, [po]);

    const totalPct = milestones.reduce((s, m) => s + (Number(m.percent) || 0), 0);

    const update = (idx, k, v) => setMilestones((p) => p.map((m, i) => i === idx ? { ...m, [k]: v } : m));
    const add = () => setMilestones((p) => [...p, { id: `tmp-${Date.now()}-${p.length}`, label: "Milestone " + (p.length + 1), percent: 0 }]);
    const remove = (idx) => setMilestones((p) => p.filter((_, i) => i !== idx));

    const save = async () => {
        if (totalPct - 100 > 0.01) { toast.error("Percents must sum to ≤ 100%"); return; }
        setSaving(true);
        try {
            await api.patch(`/billing/po/${po.id}/payment-schedule`, {
                milestones: milestones.map((m) => ({ label: m.label, percent: Number(m.percent), expected_date: m.expected_date || null })),
            });
            toast.success("Payment schedule saved");
            setOpen(false);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        } finally {
            setSaving(false);
        }
    };

    const seedDefault = () => {
        setMilestones([
            { id: "tmp-1", label: "Advance", percent: 50 },
            { id: "tmp-2", label: "UAT Acceptance", percent: 25 },
            { id: "tmp-3", label: "Final Settlement", percent: 25 },
        ]);
    };

    return (
        <div className="reedos-card p-5" data-testid="po-schedule-card">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="reedos-eyebrow flex items-center gap-2">
                        <Coins size={11} /> Payment Schedule
                    </div>
                    {(po.payment_schedule || []).length === 0 ? (
                        <div className="text-sm text-slate-500 italic mt-1">
                            No milestones defined — invoices will be raised ad-hoc.
                        </div>
                    ) : (
                        <ol className="mt-2 space-y-1">
                            {po.payment_schedule.map((m, i) => {
                                const billed = billedMilestoneIds.has(m.id);
                                return (
                                    <li key={m.id} className="flex items-center gap-3 text-sm" data-testid={`po-milestone-${i}`}>
                                        <span className="font-mono text-[11px] font-bold text-slate-700 bg-slate-100 rounded px-1.5 py-0.5">{i + 1}</span>
                                        <span className="font-semibold text-slate-900 flex-1">{m.label}</span>
                                        <span className="text-slate-600 text-xs">{m.percent}%</span>
                                        <span className="reedos-data text-xs text-slate-700 font-semibold">
                                            {formatINR(poTotalBasis * m.percent / 100, po?.currency)}
                                        </span>
                                        {billed
                                            ? <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[9px] font-bold border bg-emerald-50 text-emerald-800 border-emerald-200">INVOICED</span>
                                            : <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0 text-[9px] font-bold border bg-slate-50 text-slate-700 border-slate-300">PENDING</span>}
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
                <RoleGuard permission="po:write">
                    <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1" data-testid="po-schedule-edit-btn">
                        <PencilSimple size={12} /> Edit
                    </Button>
                </RoleGuard>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display">Payment Schedule</DialogTitle>
                        <DialogDescription>
                            Define billing milestones for this PO. Each milestone is one chunk of the PO total. Already-invoiced milestones can't be deleted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between text-xs">
                        <Button variant="ghost" size="sm" onClick={seedDefault} className="gap-1 text-slate-600" data-testid="po-schedule-seed-default-btn">
                            <Lightning size={12} /> Use 50/25/25 default
                        </Button>
                        <span className={totalPct - 100 > 0.01 ? "text-rose-700 font-bold" : "text-slate-500"}>
                            Total: {totalPct.toFixed(1)}% / 100%
                        </span>
                    </div>
                    <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="po-schedule-edit-list">
                        {milestones.map((m, idx) => {
                            const billed = billedMilestoneIds.has(m.id);
                            return (
                                <div key={m.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`po-schedule-row-${idx}`}>
                                    <span className="col-span-1 font-mono text-[11px] font-bold text-slate-700 bg-slate-100 rounded px-1.5 py-0.5 text-center">{idx + 1}</span>
                                    <Input
                                        value={m.label}
                                        onChange={(e) => update(idx, "label", e.target.value)}
                                        placeholder="Milestone name"
                                        disabled={billed}
                                        className="col-span-6 disabled:bg-slate-50"
                                    />
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={m.percent}
                                        onChange={(e) => update(idx, "percent", e.target.value)}
                                        placeholder="%"
                                        disabled={billed}
                                        className="col-span-2 font-mono text-right disabled:bg-slate-50"
                                    />
                                    <span className="col-span-2 text-[10px] text-slate-500 font-mono">
                                        {formatINR(poTotalBasis * (Number(m.percent) || 0) / 100, po?.currency)}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        disabled={billed}
                                        onClick={() => remove(idx)}
                                        className="col-span-1 text-slate-400 hover:text-rose-600 disabled:opacity-30"
                                    >
                                        <Trash size={13} />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1 self-start" data-testid="po-schedule-add-btn">
                        <Plus size={12} /> Add milestone
                    </Button>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving || totalPct - 100 > 0.01} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="po-schedule-save-btn">
                            {saving ? "Saving..." : "Save schedule"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DocumentUpload({ entityType, entityId, documents, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("entity_type", entityType);
            fd.append("entity_id", entityId);
            await api.post("/billing/document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded");
            onUploaded?.();
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };
    return (
        <div className="reedos-card" data-testid="documents-card">
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="reedos-eyebrow">Documents</div>
                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Signed PDFs &amp; attachments</h3>
            </div>
            <div className="p-5 space-y-3">
                {documents?.length > 0 ? (
                    <div className="space-y-2" data-testid="documents-list">
                        {documents.map((d) => (
                            <button
                                key={d.id}
                                type="button"
                                onClick={async () => {
                                    try { await previewDocument(d.id, { mime: d.content_type }); }
                                    catch (err) { toast.error(formatApiError(err, "Preview failed")); }
                                }}
                                className="flex w-full items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-900 transition-colors text-left"
                                data-testid={`doc-link-${d.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
                                    <div className="text-[11px] text-slate-500">{(d.size / 1024).toFixed(1)} KB · {formatDateTime(d.uploaded_at)}</div>
                                </div>
                                <FileArrowUp size={16} className="text-slate-400 shrink-0" />
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-slate-500">No documents uploaded.</div>
                )}
                <RoleGuard permission={entityType === "po" ? "po:write" : "invoice:write"}>
                    <label className="block border-2 border-dashed border-slate-200 bg-slate-50 rounded-md px-4 py-5 text-center cursor-pointer hover:border-slate-400 transition-colors" data-testid="upload-zone">
                        <FileArrowUp size={20} className="mx-auto text-slate-400" />
                        <div className="text-xs text-slate-600 mt-1.5">
                            {uploading ? "Uploading..." : "Click to upload PDF / image (≤15MB)"}
                        </div>
                        <input type="file" className="hidden" onChange={handleFile} accept=".pdf,image/*" disabled={uploading} data-testid="upload-input" />
                    </label>
                </RoleGuard>
            </div>
        </div>
    );
}

export default function PurchaseOrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [po, setPo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [newVersionOpen, setNewVersionOpen] = useState(false);
    const [newVersionRemarks, setNewVersionRemarks] = useState("");
    const [editingLine, setEditingLine] = useState(null);
    const [reassignOpen, setReassignOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/billing/po/${id}`);
            setPo(data);
            // default to final, else latest
            const def = data.versions.find((v) => v.is_final_version)
                || data.versions.reduce((a, b) => (a.version_number > b.version_number ? a : b));
            setSelectedVersionId(def?.id || null);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load PO"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const selectedVersion = useMemo(() =>
        po?.versions.find((v) => v.id === selectedVersionId) || null,
        [po, selectedVersionId]
    );

    const versionTotal = useMemo(() => {
        if (!selectedVersion) return { sub: 0, gst: 0, total: 0, software: 0, amc: 0 };
        // Iter C.2 — totals derived from EXPANDED display rows so the
        // footer matches the table. Atomic line items keep their 1:1
        // behaviour (their software_fee=amount, amc_fee=0); package
        // lines roll up their components into software/amc buckets by
        // billing_frequency. Mode-invariant: switching presentation does
        // not change totals.
        let sub = 0, gst = 0, software = 0, amc = 0;
        for (const row of expandLineItems(selectedVersion.line_items)) {
            const lineSub = row.software_fee + row.amc_fee;
            sub += lineSub;
            software += row.software_fee;
            amc += row.amc_fee;
            if (row.gst_applicable) {
                gst += lineSub * (Number(row.gst_percent) || 0) / 100;
            }
        }
        return { sub, gst, total: sub + gst, software, amc };
    }, [selectedVersion]);

    // Iter C.2 — Re-presentation of a package line item. Operator can flip
    // mode (itemized/consolidated/hybrid) on the PO without re-authoring
    // the underlying components. PATCH endpoint captures the change in the
    // audit log so the original Proposal mode is reconstructible.
    //
    // Per-component visibility (`is_visible_in_print`) is a PRESENTATION
    // attribute (not structural — amounts/activation/etc remain immutable
    // per Phase C D3). When the operator picks Hybrid, we apply a sensible
    // default split: One-time components stay visible (commercial setup
    // items), recurring components get bundled into the rollup. For
    // Itemized + Consolidated all components are marked visible (each is
    // either its own row, or all are folded into the single consolidated
    // row — visibility flag is moot in either case but keeping a uniform
    // post-flip state makes subsequent flips deterministic).
    const repackageLineItem = async (li, nextMode, nextConsolidatedLabel) => {
        try {
            const baseComponents = li.package_components || [];
            const nextComponents = baseComponents.map((c) => {
                if (nextMode === "hybrid") {
                    return { ...c, is_visible_in_print: c.billing_frequency === "One-time" };
                }
                return { ...c, is_visible_in_print: true };
            });
            const payload = {
                product_name: li.product_name,
                product_type: li.product_type || "One-time",
                amount: Number(li.amount) || 0,
                amc_amount: Number(li.amc_amount) || 0,
                billing_frequency: li.billing_frequency || "Annual",
                gst_applicable: li.gst_applicable !== false,
                gst_percent: Number(li.gst_percent) || 0,
                start_date: li.start_date || null,
                end_date: li.end_date || null,
                hsn_sac_code: li.hsn_sac_code || "997331",
                option_label: li.option_label || null,
                option_description: li.option_description || null,
                package_source_id: li.package_source_id,
                package_source_code: li.package_source_code,
                package_presentation_mode: nextMode,
                package_consolidated_label: nextMode === "consolidated"
                    ? (nextConsolidatedLabel || li.package_consolidated_label || li.product_name)
                    : null,
                package_components: nextComponents,
            };
            await api.patch(
                `/billing/po/${po.id}/lineitem/${li.id}?version_id=${selectedVersion.id}`,
                payload,
            );
            toast.success(`Presentation mode → ${nextMode}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to change presentation mode"));
        }
    };

    const addNewVersion = async () => {
        try {
            const { data } = await api.post(`/billing/po/${id}/version/add`, { remarks: newVersionRemarks, line_items: [] });
            toast.success(`V${data.version_number} created`);
            setNewVersionOpen(false);
            setNewVersionRemarks("");
            await load();
            setSelectedVersionId(data.id);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to add version"));
        }
    };

    const removeLineItem = async (li) => {
        if (!window.confirm(`Remove "${li.product_name}" from this version?`)) return;
        try {
            await api.delete(`/billing/po/${id}/lineitem/${li.id}?version_id=${selectedVersionId}`);
            toast.success("Line item removed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to remove line item"));
        }
    };

    if (loading) {
        return (
            <div data-testid="po-detail-page">
                <PageHeader eyebrow="Purchase order" title="Loading..." />
            </div>
        );
    }

    if (!po) return <div className="p-8 text-sm text-slate-500">PO not found.</div>;

    const isLocked = po.is_final;
    const versionLocked = selectedVersion?.is_final_version;

    return (
        <div data-testid="po-detail-page">
            <PageHeader
                eyebrow={
                    <Link to="/purchase-orders" className="inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft size={12} /> Purchase Orders
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-2xl sm:text-3xl">{po.po_number}</span>
                        {isLocked ? <StatusBadge status="Locked" icon={<LockKey size={11} weight="fill" />} /> : <StatusBadge status="Draft" />}
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2 text-xs flex-wrap">
                        <Buildings size={14} /> {po.client?.name}
                        {po.client?.gstin && <span className="font-mono text-xs ml-1.5 text-slate-400">· {po.client.gstin}</span>}
                        {po.owner && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Owner</span>
                                <OwnerCell
                                    owner={po.owner}
                                    testId="po-detail-owner"
                                    onReassignClick={isSuperAdmin ? () => setReassignOpen(true) : undefined}
                                />
                            </>
                        )}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        {isLocked && (
                            <RoleGuard permission="invoice:write">
                                <GenerateInvoiceButton po={po} onCreated={(inv) => navigate(`/invoices/${inv.id}`)} />
                            </RoleGuard>
                        )}
                        {!isLocked && selectedVersion && (
                            <RoleGuard permission="po:write">
                                <FinalizeButton poId={po.id} version={selectedVersion} onDone={load} />
                            </RoleGuard>
                        )}
                        {!isLocked && selectedVersion && (
                            <RoleGuard permission="po:write">
                                <CloneVersionButton poId={po.id} versionId={selectedVersion.id} onCloned={load} />
                            </RoleGuard>
                        )}
                        {!isLocked && (
                            <RoleGuard permission="po:write">
                                <Dialog open={newVersionOpen} onOpenChange={setNewVersionOpen}>
                                    <DialogTrigger asChild>
                                        <Button size="sm" variant="outline" className="gap-1.5" data-testid="new-version-btn">
                                            <Plus size={14} /> New version
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle className="font-display">New version</DialogTitle>
                                            <DialogDescription>Creates an empty version. Add line items afterwards, or use Clone instead.</DialogDescription>
                                        </DialogHeader>
                                        <div>
                                            <Label className="text-xs font-semibold">Remarks</Label>
                                            <Textarea value={newVersionRemarks} onChange={(e) => setNewVersionRemarks(e.target.value)} rows={2} className="mt-1.5" data-testid="new-version-remarks" />
                                        </div>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setNewVersionOpen(false)}>Cancel</Button>
                                            <Button onClick={addNewVersion} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-version-confirm">Create version</Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </RoleGuard>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
                {/* Left: timeline */}
                <div className="space-y-6">
                    <VersionTimeline po={po} selectedId={selectedVersionId} onSelect={setSelectedVersionId} />
                    <DocumentUpload entityType="po" entityId={po.id} documents={po.documents} onUploaded={load} />
                </div>

                {/* Right: selected version detail */}
                {selectedVersion && (
                    <div className="space-y-5 animate-slide-up">
                        {/* Proposal linkage badge */}
                        {po.from_proposal_id && (
                            <Link
                                to={`/proposals/${po.from_proposal_id}`}
                                className="reedos-card px-5 py-3 flex items-center gap-3 hover:border-slate-400 transition-colors"
                                data-testid="po-proposal-link"
                            >
                                <FileText size={20} className="text-slate-500" weight="duotone" />
                                <div className="flex-1 min-w-0">
                                    <div className="reedos-eyebrow">REEDOS Proposal No</div>
                                    <div className="font-mono text-sm font-semibold text-slate-900 mt-0.5">
                                        {po.from_proposal_number || po.from_proposal_id}
                                        {po.from_proposal_version_number && (
                                            <span className="text-slate-400 ml-2">V{po.from_proposal_version_number}</span>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight size={14} className="text-slate-400" />
                            </Link>
                        )}

                        {/* Client-PO reference */}
                        <ClientPoRefCard po={po} editable={!po.is_final || true /* always editable */} onChanged={load} />

                        {/* Multi-PO coverage — which proposal charges this PO covers. */}
                        <PoProposalCoverageCard po={po} />

                        {/* Billing status (only after finalize) */}
                        {isLocked && <PoBillingStatusCard po={po} />}

                        {/* POAGG-AMC-001 v2 (2026-02-15) — AMC Pool Panel
                            Renders only when the PO carries AMC value AND is finalized.
                            Owns project completion recording, AMC activation, and
                            linked-schedule visibility. */}
                        {isLocked && po?.billing_status?.has_amc_value && (
                            <AmcPoolPanel po={po} onChanged={load} />
                        )}

                        {/* Annual Cost — separate subscription pool. */}
                        {isLocked && po?.billing_status?.has_subscription_value && (
                            <SubscriptionPoolPanel po={po} onChanged={load} />
                        )}

                        {/* Payment Schedule */}
                        <PaymentScheduleCard po={po} editable={true} onChanged={load} />

                        {/* Iter C.2.1.e — Special Offer (frozen snapshot from
                            the source proposal on Proposal-derived POs). Renders
                            nothing when no offer was applied. */}
                        <SpecialOfferDisplay po={po} />

                        <div className="reedos-card">
                            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div>
                                    <div className="reedos-eyebrow flex items-center gap-2">
                                        Version {selectedVersion.version_number}
                                        {versionLocked && <StatusBadge status="Locked" icon={<LockKey size={10} weight="fill" />} />}
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">
                                        {selectedVersion.line_items.length} line item{selectedVersion.line_items.length !== 1 ? "s" : ""}
                                    </h3>
                                    {selectedVersion.remarks && (
                                        <p className="text-sm text-slate-500 mt-1">{selectedVersion.remarks}</p>
                                    )}
                                </div>
                                {!versionLocked && !isLocked && (
                                    <RoleGuard permission="po:write">
                                        <LineItemDialog poId={po.id} versionId={selectedVersion.id} onAdded={load} />
                                    </RoleGuard>
                                )}
                            </div>

                            {selectedVersion.line_items.length === 0 ? (
                                <div className="px-5 py-12 text-center">
                                    <div className="text-sm text-slate-500">No line items yet.</div>
                                    {!versionLocked && !isLocked && (
                                        <div className="mt-3">
                                            <RoleGuard permission="po:write">
                                                <LineItemDialog poId={po.id} versionId={selectedVersion.id} onAdded={load} />
                                            </RoleGuard>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <table className="w-full text-sm" data-testid="line-items-table">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr className="text-left">
                                            <th className="px-4 py-3 reedos-eyebrow">Item</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">Amount</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right" data-testid="po-recurring-heading">
                                                {(selectedVersion.line_items || []).some(usesAnnualCost) ? "AMC / Annual Cost" : "AMC"}
                                            </th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">GST</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">Line total</th>
                                            {!versionLocked && !isLocked && (
                                                <th className="px-4 py-3 reedos-eyebrow text-right w-24">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Iter C.2 — Same expansion pattern as ProposalDetail.jsx.
                                            // Package banner row emitted once per package head with an
                                            // inline mode-picker for re-presentation; component rows
                                            // rendered underneath with the ↳ indent + scope list for
                                            // consolidated / hybrid-rollup variants.
                                            const editable = !versionLocked && !isLocked;
                                            const colSpan = editable ? 6 : 5;
                                            const parentById = new Map();
                                            for (const li of selectedVersion.line_items) parentById.set(li.id, li);
                                            const rows = [];
                                            const emittedPkgHead = new Set();
                                            for (const li of selectedVersion.line_items) {
                                                for (const dr of expandLineItem(li)) {
                                                    const parentLi = parentById.get(dr.parent_id);
                                                    const isPkgRow = dr.is_derived && parentLi;
                                                    if (isPkgRow && !emittedPkgHead.has(dr.parent_id)) {
                                                        emittedPkgHead.add(dr.parent_id);
                                                        const currentMode = parentLi.package_presentation_mode || "itemized";
                                                        rows.push(
                                                            <tr key={`pkghdr-${dr.parent_id}`} className="bg-violet-50/70 border-t border-violet-200" data-testid={`pkg-head-${dr.parent_id}`}>
                                                                <td colSpan={colSpan} className="px-4 py-2">
                                                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                                                        <div className="flex flex-col min-w-0 gap-1">
                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.16em] uppercase bg-violet-200 text-violet-900">Package</span>
                                                                                <span className="font-semibold text-slate-900 truncate">{parentLi.product_name}</span>
                                                                                {parentLi.package_presentation_mode === "consolidated" && parentLi.package_consolidated_label && (
                                                                                    <span className="text-[10px] text-slate-500">· "{parentLi.package_consolidated_label}"</span>
                                                                                )}
                                                                            </div>
                                                                            {/* Iter C.2.1.CAP — Capability badges on PO package head. */}
                                                                            <CapabilityBadges capabilities={parentLi.capabilities} size="sm" />
                                                                            {/* Iter C.2.1.d — Commitments chip strip on PO package head. */}
                                                                            <CommitmentsBadgeStrip commitments={parentLi.commitments} />
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Re-present</span>
                                                                            <select
                                                                                value={currentMode}
                                                                                onChange={(e) => {
                                                                                    const next = e.target.value;
                                                                                    if (next === currentMode) return;
                                                                                    if (next === "consolidated") {
                                                                                        const label = window.prompt(
                                                                                            "Consolidated label (client-facing single line):",
                                                                                            parentLi.package_consolidated_label || parentLi.product_name || "",
                                                                                        );
                                                                                        if (!label || !label.trim()) return;
                                                                                        repackageLineItem(parentLi, next, label.trim());
                                                                                    } else {
                                                                                        repackageLineItem(parentLi, next, null);
                                                                                    }
                                                                                }}
                                                                                disabled={!editable}
                                                                                className="h-7 px-2 text-xs border border-violet-200 rounded bg-white focus:border-violet-400 disabled:opacity-50"
                                                                                data-testid={`pkg-mode-${dr.parent_id}`}
                                                                            >
                                                                                {PRESENTATION_MODE_OPTIONS.map((o) => (
                                                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                                                ))}
                                                                            </select>
                                                                            {editable && (
                                                                                <RoleGuard permission="po:write">
                                                                                    <div className="inline-flex items-center gap-0.5 shrink-0">
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() => setEditingLine(parentLi)}
                                                                                            className="h-7 px-2 text-slate-600 hover:text-slate-900"
                                                                                            title="Edit package metadata (HSN/GST)"
                                                                                            data-testid={`pkg-edit-${dr.parent_id}`}
                                                                                        >
                                                                                            <PencilSimple size={13} />
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            onClick={() => removeLineItem(parentLi)}
                                                                                            className="h-7 px-2 text-slate-400 hover:text-rose-600"
                                                                                            title="Remove package"
                                                                                            data-testid={`pkg-delete-${dr.parent_id}`}
                                                                                        >
                                                                                            <Trash size={13} />
                                                                                        </Button>
                                                                                    </div>
                                                                                </RoleGuard>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }

                                                    const annualFee = Number(dr.annual_cost_fee) || 0;
                                                    const lineSub = dr.software_fee + dr.amc_fee + annualFee;
                                                    const lineTotal = lineSub * (1 + (dr.gst_applicable ? (Number(dr.gst_percent) || 0) / 100 : 0));
                                                    // Iter 107.1 — Row-type × presentation-mode branching
                                                    // brought over from ProposalDetail.jsx (~L2079-2108) so
                                                    // the PO renderer honours the same row hierarchy as its
                                                    // upstream Proposal renderer. Product-header rows are
                                                    // grouping rows — Amount / AMC / GST / Line Total must
                                                    // be blank. Module rows render only in itemized mode;
                                                    // atomic rows render in itemized + hybrid, blank in
                                                    // consolidated. Non-Builder (legacy) rows fall through
                                                    // to the original ``—`` placeholder unchanged.
                                                    const isBuilderRow = !!dr.presentation_mode;
                                                    const builderRowKind = dr.row_kind;
                                                    const isProductHeader = isBuilderRow && builderRowKind === "product_header";
                                                    const builderMode = dr.presentation_mode;
                                                    const builderShowModulePrices = builderMode === "itemized";
                                                    const builderShowAtomicPrices = builderMode !== "consolidated";
                                                    const formatBuilderCell = (val, visible) => {
                                                        if (!visible) return "";
                                                        return val > 0 ? formatINR(val, po?.currency) : "";
                                                    };
                                                    const showSetupCell = !isBuilderRow
                                                        || (builderRowKind === "module" ? builderShowModulePrices
                                                            : builderRowKind === "atomic" ? builderShowAtomicPrices
                                                                : false);  // product_header
                                                    const showAmcCell = showSetupCell;
                                                    const showLineTotalCell = showSetupCell;
                                                    const setupCellContent = isBuilderRow
                                                        ? formatBuilderCell(dr.software_fee, showSetupCell)
                                                        : (dr.software_fee > 0 ? formatINR(dr.software_fee, po?.currency) : <span className="text-slate-300">—</span>);
                                                    const annualTag = <span className="block text-[9px] font-bold uppercase tracking-wide text-sky-700">Annual Cost</span>;
                                                    const amcCellContent = dr.annual_cost_complimentary
                                                        ? <span data-testid={`po-line-annual-cost-${dr.id}`}>{annualTag}<span className="italic text-indigo-700">Complimentary</span></span>
                                                        : annualFee > 0
                                                            ? <span data-testid={`po-line-annual-cost-${dr.id}`}>{annualTag}{formatBuilderCell(annualFee, showAmcCell || !isBuilderRow)}</span>
                                                            : isBuilderRow
                                                                ? formatBuilderCell(dr.amc_fee, showAmcCell)
                                                                : (dr.amc_fee > 0 ? formatINR(dr.amc_fee, po?.currency) : <span className="text-slate-300">—</span>);
                                                    const lineTotalCellContent = isBuilderRow
                                                        ? (showLineTotalCell && lineTotal > 0 ? formatINR(lineTotal, po?.currency) : "")
                                                        : formatINR(lineTotal, po?.currency);
                                                    const rowClass = isPkgRow
                                                        ? (dr.kind === "pkg-hybrid-rollup"
                                                            ? "border-t border-slate-100 bg-amber-50/30"
                                                            : "border-t border-slate-100 bg-violet-50/20")
                                                        : (builderRowKind === "module"
                                                            ? "border-t border-slate-100 bg-slate-50/40"
                                                            : builderRowKind === "product_header"
                                                                ? "border-t border-slate-200 bg-slate-50"
                                                                : "border-t border-slate-100");
                                                    rows.push(
                                                        <tr key={dr.id} className={rowClass} data-testid={`po-line-row-${dr.id}`}>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-start gap-2">
                                                                    {(isPkgRow || builderRowKind === "module") && (
                                                                        <span aria-hidden className="text-slate-400 text-xs leading-5 select-none">↳</span>
                                                                    )}
                                                                    <div className="min-w-0">
                                                                        <div className={`font-medium text-slate-900 truncate ${isProductHeader ? "font-semibold" : ""}`}>{dr.product_name}</div>
                                                                        {dr.product_subtitle && (
                                                                            <div className="text-[11px] text-slate-500 mt-0.5">{dr.product_subtitle}</div>
                                                                        )}
                                                                        {/* Iter C.2.1.CAP — Capability badges on atomic PO lines. */}
                                                                        {!isPkgRow && (
                                                                            <CapabilityBadges capabilities={parentLi?.capabilities} size="sm" className="mt-1" />
                                                                        )}
                                                                        {/* Iter C.2.1.d — Commitments chip strip on atomic PO lines. */}
                                                                        {!isPkgRow && (
                                                                            <CommitmentsBadgeStrip commitments={parentLi?.commitments} />
                                                                        )}
                                                                        {Array.isArray(dr.scope_items) && dr.scope_items.length > 0 && (
                                                                            <div className="mt-1.5" data-testid={`po-line-scope-${dr.id}`}>
                                                                                <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Includes</div>
                                                                                <ul className="mt-0.5 space-y-0.5 text-[12px] text-slate-700">
                                                                                    {dr.scope_items.map((s, i) => (
                                                                                        <li key={i} className="flex items-baseline gap-1.5">
                                                                                            <span className="text-slate-400">•</span>
                                                                                            <span className="font-medium">{s.module_label}</span>
                                                                                            {s.channels && (
                                                                                                <span className="text-slate-500 text-[11px]">({formatScopeChannels(s.channels)})</span>
                                                                                            )}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm font-semibold">{setupCellContent}</td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm">{amcCellContent}</td>
                                                            <td className="px-4 py-3 text-right text-xs text-slate-600">{isProductHeader ? "" : (dr.gst_applicable ? `${dr.gst_percent}%` : "Excl.")}</td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm font-semibold">{lineTotalCellContent}</td>
                                                            {editable && (
                                                                <td className="px-4 py-3 text-right">
                                                                    {isPkgRow ? (
                                                                        <span className="text-[10px] text-slate-400">—</span>
                                                                    ) : (
                                                                        <RoleGuard permission="po:write">
                                                                            <div className="inline-flex items-center gap-0.5">
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={() => setEditingLine(parentLi)}
                                                                                    className="h-7 px-2 text-slate-600 hover:text-slate-900"
                                                                                    title="Edit line item"
                                                                                    data-testid={`po-line-edit-${dr.id}`}
                                                                                >
                                                                                    <PencilSimple size={13} />
                                                                                </Button>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={() => removeLineItem(parentLi)}
                                                                                    className="h-7 px-2 text-slate-400 hover:text-rose-600"
                                                                                    title="Remove line item"
                                                                                    data-testid={`po-line-delete-${dr.id}`}
                                                                                >
                                                                                    <Trash size={13} />
                                                                                </Button>
                                                                            </div>
                                                                        </RoleGuard>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                }
                                            }
                                            return rows;
                                        })()}
                                    </tbody>
                                    <tfoot className="border-t border-slate-200 bg-slate-50">
                                        {/* Iter 67 — Special-Offer-aware footer.
                                            When the selected version is the
                                            final/locked version AND an offer is
                                            applied on the PO, render the five-row
                                            commercial summary (Gross → Less Offer
                                            → Taxable → GST → Total) reading
                                            authoritative figures from
                                            ``billing_status.net_*`` so the values
                                            exactly match the invoices generated
                                            from this PO. Otherwise the legacy
                                            three-row footer remains. */}
                                        {(() => {
                                            const offerActive = versionLocked && po.special_offer && po.special_offer.enabled;
                                            const sub = versionTotal.sub;
                                            const gst = offerActive
                                                ? (po.billing_status?.net_po_gst ?? versionTotal.gst)
                                                : versionTotal.gst;
                                            const total = offerActive
                                                ? (po.billing_status?.net_po_total ?? versionTotal.total)
                                                : versionTotal.total;
                                            return (
                                                <CommercialSummaryRows
                                                    subtotal={sub}
                                                    gst={gst}
                                                    total={total}
                                                    specialOffer={offerActive ? po.special_offer : null}
                                                    currency={po?.currency}
                                                    colSpan={!versionLocked && !isLocked ? 4 : 3}
                                                    testIdPrefix="po-footer"
                                                />
                                            );
                                        })()}
                                    </tfoot>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Edit dialog (controlled) — rendered once per page so it floats above the line table */}
            {editingLine && selectedVersion && (
                <LineItemDialog
                    poId={po.id}
                    versionId={selectedVersion.id}
                    initial={editingLine}
                    open={!!editingLine}
                    onOpenChange={(v) => { if (!v) setEditingLine(null); }}
                    onAdded={() => { setEditingLine(null); load(); }}
                />
            )}
            <ReassignOwnerDialog
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                entityLabel="Purchase Order"
                entityName={po.po_number}
                currentOwner={po.owner}
                reassignUrl={`/billing/po/${id}/owner`}
                onSuccess={load}
                testIdPrefix="po-detail-reassign"
            />
        </div>
    );
}
